import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Button, Card, Col, DatePicker, Drawer, Row, Space, Switch, Table, Tabs, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { AndroidOutlined, DesktopOutlined, GiftOutlined, ReloadOutlined, RiseOutlined, TeamOutlined, UserAddOutlined, WindowsOutlined } from '@ant-design/icons';
import { useViewScope } from '../../utils/viewScope';
import {
  loadCohortRedemptions,
  loadDailyMembers,
  loadOnline,
  loadOperationsCapabilities,
  loadSummary,
  loadTopAccountDevices,
  loadTopAccounts,
  type OperationsCapabilities,
} from './api';
import { OperationsComboChart, ActivityDayBars } from './OperationsCharts';
import { KshieldLegacyObservations } from './KshieldLegacyObservations';
import { DetailMetric } from './DetailMetric';
import { ProtocolUsagePanel } from './ProtocolUsagePanel';
import './operations.css';
import {
  formatDecimalGb,
  formatMetric,
  formatTopAccountCount,
  operationsTrendForRange,
  LatestScopeRequest,
  leaderboardScopeKey,
  liveScopeKey,
  periodScopeKey,
  taipeiWindow,
  type CohortRow,
  type DataValue,
  type DeviceUsageRow,
  type MemberListRow,
  type OperationKpi,
  type OperationsOnline,
  type OperationsSummary,
  type RedemptionRow,
  type SnapshotList,
  type TopAccountRow,
  type TopAccountsPage,
} from './lib';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function Metric({ data, suffix = '' }: { data?: DataValue<number> | null; suffix?: string }) {
  return <Text>{formatMetric(data, suffix)}</Text>;
}

function Cutoff({ value, label = '截至' }: { value?: string | null; label?: string }) {
  return <Text type="secondary">{label}：{value ? new Date(value).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }) : '—'}</Text>;
}

function Pie({ data, period }: { data?: OperationsSummary['platformDistribution']; period: string }) {
  const entries = [
    { key: 'apkOnly', label: '僅 APK 使用', color: '#35c68c' },
    { key: 'windowsOnly', label: '僅 Windows 使用', color: '#43a5f7' },
    { key: 'both', label: '同時使用（多設備）', color: '#9c84f6' },
    { key: 'otherUnknown', label: '其他／未知', color: '#8c8c8c' },
  ] as const;
  const hasValues = entries.every(item => data?.[item.key]?.value !== null && data?.[item.key]?.value !== undefined && Number.isFinite(data[item.key].value));
  const total = hasValues ? entries.reduce((sum, item) => sum + (data?.[item.key]?.value ?? 0), 0) : 0;
  let offset = 0;
  return <div className="operations-donut-layout">
    {hasValues && total > 0 ? <svg role="img" aria-label="平台使用會員互斥分布" viewBox="0 0 120 120" className="operations-donut">
      {entries.map(item => {
        const fraction = (data?.[item.key]?.value ?? 0) / total;
        const dash = `${fraction * 100} ${100 - fraction * 100}`;
        const start = offset;
        offset += fraction * 100;
        return <circle key={item.key} cx="60" cy="60" r="40" fill="none" stroke={item.color} strokeWidth="20" pathLength="100" strokeDasharray={dash} strokeDashoffset={-start} transform="rotate(-90 60 60)">
          <title>{item.label}：{Math.round(fraction * 100)}%</title>
        </circle>;
      })}
      <text x="60" y="60" textAnchor="middle" fontSize="16" fontWeight="700" fill="#102b55">{new Intl.NumberFormat('zh-TW').format(total)}</text>
      <text x="60" y="72" textAnchor="middle" fontSize="8" fill="#637796">總活躍用戶</text>
      <text x="60" y="82" textAnchor="middle" fontSize="8" fill="#637796">（{period}）</text>
    </svg> : <Text>{hasValues ? '0 人' : '—'}</Text>}
    <div className="operations-donut-legend">{entries.map(item => <span key={item.key}>
      <span style={{ display: 'inline-block', width: 9, height: 9, marginRight: 6, background: item.color }} />{item.label}：<Metric data={data?.[item.key]} />
      <Text type="secondary"> {hasValues && total > 0 ? `${(((data?.[item.key]?.value ?? 0) / total) * 100).toFixed(1)}%` : '—'}</Text>
    </span>)}</div>
  </div>;
}

type Detail =
  | { kind: 'members'; title: string; brand: string; date: string; metric: string; snapshotId: string }
  | { kind: 'redemptions'; title: string; brand: string; date: string; snapshotId: string }
  | { kind: 'devices'; title: string; brand: string; memberAccountId: number; snapshotId: string };

function DetailDrawer({ detail, onClose, clearNames }: { detail: Detail | null; onClose: () => void; clearNames: boolean }) {
  const [result, setResult] = useState<SnapshotList<MemberListRow | RedemptionRow | DeviceUsageRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => { setCursor(null); setResult(null); setError(null); }, [detail, clearNames]);
  useEffect(() => {
    if (!detail) return;
    const controller = new AbortController();
    setLoading(true);
    const request = detail.kind === 'members'
      ? loadDailyMembers(detail.brand, detail.date, detail.snapshotId, detail.metric, cursor, controller.signal, clearNames)
      : detail.kind === 'redemptions'
        ? loadCohortRedemptions(detail.brand, detail.date, detail.snapshotId, cursor, controller.signal, clearNames)
        : loadTopAccountDevices(detail.brand, detail.memberAccountId, detail.snapshotId, cursor, controller.signal);
    request.then(data => { if (!controller.signal.aborted) { setResult(data); setError(null); } })
      .catch(error => { if (!controller.signal.aborted) { setResult(null); setError(error instanceof Error ? error.message : '讀取失敗'); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [detail, cursor, clearNames]);
  const columns: TableProps<MemberListRow | RedemptionRow | DeviceUsageRow>['columns'] = detail?.kind === 'devices'
    ? [
      { title: '裝置', render: (_, row) => 'deviceLabel' in row ? row.deviceLabel : '—' },
      { title: '平台', render: (_, row) => 'platform' in row ? row.platform : '—' },
      { title: '上傳', render: (_, row) => 'uploadBytes' in row ? formatDecimalGb(row.uploadBytes) : '—' },
      { title: '下載', render: (_, row) => 'downloadBytes' in row ? formatDecimalGb(row.downloadBytes) : '—' },
      { title: '總量', render: (_, row) => 'totalBytes' in row ? formatDecimalGb(row.totalBytes) : '—' },
    ]
    : [
      { title: '品牌', render: (_, row) => 'brand' in row ? row.brand : '—' },
      { title: '會員', render: (_, row) => 'accountLabel' in row ? row.accountLabel : '—' },
      { title: '會員 ID', render: (_, row) => 'memberAccountId' in row ? row.memberAccountId : '—' },
      ...(detail?.kind === 'redemptions' ? [{ title: '發放時間', render: (_: unknown, row: MemberListRow | RedemptionRow | DeviceUsageRow) => 'issuedAt' in row ? row.issuedAt : '—' }] : []),
    ];
  return <Drawer title={detail?.title} open={!!detail} onClose={onClose} width={820} destroyOnClose>
    {detail && <>
      <Cutoff value={result?.cutoffAt} />
      {error && <Alert type="warning" showIcon message="資料未提供" description={error} style={{ marginTop: 12 }} />}
      <Table rowKey={(row, index) => 'entitlementId' in row ? `e-${row.entitlementId}` : 'memberAccountId' in row ? `${row.brand}-${row.memberAccountId}` : `d-${index}`} columns={columns} dataSource={result?.rows ?? []} loading={loading} pagination={false} style={{ marginTop: 16 }} />
      <Space style={{ marginTop: 12 }}><Text>共 {result?.total ?? '—'} 筆</Text>{result?.nextCursor && <Button onClick={() => setCursor(result.nextCursor ?? null)}>下一頁</Button>}</Space>
    </>}
  </Drawer>;
}

function OperationsOverview({ range, setRange }: { range: { from: string; through: string }; setRange: (range: { from: string; through: string }) => void }) {
  const { scope: brand } = useViewScope();
  const [summaryState, setSummaryState] = useState<{ key: string; value: OperationsSummary | null; error: string | null } | null>(null);
  const [onlineState, setOnlineState] = useState<{ key: string; value: OperationsOnline | null; error: string | null } | null>(null);
  const [topState, setTopState] = useState<{ key: string; value: TopAccountsPage | null; error: string | null } | null>(null);
  const [topPage, setTopPage] = useState(1);
  const [topSnapshot, setTopSnapshot] = useState<{ brand: string; id: string } | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [capabilitiesState, setCapabilitiesState] = useState<{ brand: string; value: OperationsCapabilities | null }>({ brand: '', value: null });
  const [clearNames, setClearNames] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const summaryGuard = useRef(new LatestScopeRequest());
  const onlineGuard = useRef(new LatestScopeRequest());
  const topGuard = useRef(new LatestScopeRequest());

  const summaryKey = periodScopeKey(brand, range.from, range.through, refresh);
  const onlineKey = liveScopeKey(brand, refresh);
  const activeTopSnapshot = topSnapshot?.brand === brand ? topSnapshot.id : null;
  const capabilities = capabilitiesState.brand === brand ? capabilitiesState.value : null;
  const canMemberDetail = capabilities?.canMemberDetail === true;
  const showClearNames = capabilities?.canClearAccount === true && clearNames;
  const topKey = `${leaderboardScopeKey(brand, topPage, activeTopSnapshot, refresh)}:${showClearNames ? 'clear' : 'masked'}`;
  const summary = summaryState?.key === summaryKey ? summaryState.value : null;
  const online = onlineState?.key === onlineKey ? onlineState.value : null;
  const top = topState?.key === topKey ? topState.value : null;
  const activeDetail = detail && (
    detail.kind === 'devices'
      ? (detail.brand === brand || brand === 'all') && detail.snapshotId === top?.snapshotId
      : detail.brand === brand && detail.snapshotId === summary?.snapshotId
  ) ? detail : null;
  useEffect(() => { setTopPage(1); setTopSnapshot(null); setDetail(null); setClearNames(false); }, [brand]);
  useEffect(() => { setDetail(null); }, [range.from, range.through]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTopPage(1);
      setTopSnapshot(null);
      setRefresh(value => value + 1);
    }, 300_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setCapabilitiesState({ brand, value: null });
    loadOperationsCapabilities(brand, controller.signal)
      .then(value => { if (!controller.signal.aborted) setCapabilitiesState({ brand, value }); })
      .catch(() => { if (!controller.signal.aborted) setCapabilitiesState({ brand, value: null }); });
    return () => controller.abort();
  }, [brand]);

  useEffect(() => {
    const controller = new AbortController();
    const request = summaryGuard.current.begin(summaryKey);
    setSummaryState({ key: summaryKey, value: null, error: null });
    loadSummary(brand, range.from, range.through, controller.signal)
      .then(value => { if (summaryGuard.current.isCurrent(request, summaryKey)) setSummaryState({ key: summaryKey, value, error: null }); })
      .catch(error => { if (!controller.signal.aborted && summaryGuard.current.isCurrent(request, summaryKey)) setSummaryState({ key: summaryKey, value: null, error: error instanceof Error ? error.message : '讀取失敗' }); });
    return () => { controller.abort(); summaryGuard.current.invalidate(); };
  }, [summaryKey, brand, range.from, range.through]);

  useEffect(() => {
    const controller = new AbortController();
    setOnlineState({ key: onlineKey, value: null, error: null });
    const poll = () => {
      const request = onlineGuard.current.begin(onlineKey);
      void loadOnline(brand, controller.signal)
        .then(value => { if (onlineGuard.current.isCurrent(request, onlineKey)) setOnlineState({ key: onlineKey, value, error: null }); })
        .catch(error => { if (!controller.signal.aborted && onlineGuard.current.isCurrent(request, onlineKey)) setOnlineState({ key: onlineKey, value: null, error: error instanceof Error ? error.message : '讀取失敗' }); });
    };
    poll();
    const timer = window.setInterval(poll, 30_000);
    return () => { window.clearInterval(timer); controller.abort(); onlineGuard.current.invalidate(); };
  }, [onlineKey, brand]);

  useEffect(() => {
    const controller = new AbortController();
    const request = topGuard.current.begin(topKey);
    setTopState({ key: topKey, value: null, error: null });
    loadTopAccounts(brand, topPage, 20, activeTopSnapshot, controller.signal, showClearNames)
      .then(value => {
        if (!topGuard.current.isCurrent(request, topKey)) return;
        setTopState({ key: topKey, value, error: null });
      })
      .catch(error => { if (!controller.signal.aborted && topGuard.current.isCurrent(request, topKey)) setTopState({ key: topKey, value: null, error: error instanceof Error ? error.message : '讀取失敗' }); });
    return () => { controller.abort(); topGuard.current.invalidate(); };
  }, [topKey, brand, topPage, activeTopSnapshot, showClearNames]);

  const openDailyMembers = (date: string, metric: string, title: string) => {
    if (!summary) return;
    setDetail({ kind: 'members', title: `${date} ${title}`, brand, date, metric, snapshotId: summary.snapshotId });
  };

  const dailyColumns: TableProps<OperationsSummary['daily'][number]>['columns'] = [
    { title: '日期', dataIndex: 'date', fixed: 'left' },
    { title: '新會員', render: (_, row) => <DetailMetric data={row.newMembers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.date, 'new_members', '新會員')} /> },
    { title: '同日兌換', render: (_, row) => <DetailMetric data={row.sameDayRedeemers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.date, 'same_day_redeemers', '同日兌換會員')} /> },
    { title: '已辨識會員觀測尖峰', render: (_, row) => <Metric data={row.observedPeakMembers} /> },
    { title: '已辨識裝置觀測尖峰', render: (_, row) => <Metric data={row.observedPeakDevices} /> },
    { title: '已辨識 APK 使用會員／裝置', render: (_, row) => <><DetailMetric data={row.apkMembers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.date, 'apk_use_members', '已辨識 APK 使用會員')} />／<Metric data={row.apkDevices} /></> },
    { title: '已辨識 Windows 使用會員／裝置', render: (_, row) => <><DetailMetric data={row.windowsMembers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.date, 'windows_use_members', '已辨識 Windows 使用會員')} />／<Metric data={row.windowsDevices} /></> },
    { title: '實際使用會員', render: (_, row) => <DetailMetric data={row.totalUseMembers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.date, 'actual_use_members', '實際使用會員')} /> },
  ];
  const cohortColumns: TableProps<CohortRow>['columns'] = [
    { title: '註冊日期', dataIndex: 'registrationDay' },
    { title: '註冊會員', render: (_, row) => <DetailMetric data={row.registrations} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.registrationDay, 'new_members', '註冊會員')} /> },
    { title: '同日兌換／率', render: (_, row) => <><DetailMetric data={row.sameDayRedeemers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.registrationDay, 'same_day_redeemers', '同日兌換會員')} />／<Metric data={row.sameDayRate} suffix="%" /></> },
    { title: '累計兌換／率', render: (_, row) => <><DetailMetric data={row.cumulativeRedeemers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.registrationDay, 'cohort_redeemed', '累計兌換會員')} />／<Metric data={row.cumulativeRate} suffix="%" /></> },
    { title: '未兌換', render: (_, row) => <DetailMetric data={row.cumulativeUnredeemed} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.registrationDay, 'cohort_unredeemed', '未兌換會員')} /> },
    { title: '成功兌換筆數', render: (_, row) => <DetailMetric data={row.successfulRedemptionRecords} canOpen={canMemberDetail && !!summary} onClick={() => summary && setDetail({ kind: 'redemptions', title: `${row.registrationDay} 註冊 cohort 兌換`, brand, date: row.registrationDay, snapshotId: summary.snapshotId })} /> },
    { title: '兌換後使用／率', render: (_, row) => <><DetailMetric data={row.postRedemptionUseMembers} canOpen={canMemberDetail && !!summary} onClick={() => openDailyMembers(row.registrationDay, 'cohort_post_redemption_use', '兌換後使用會員')} />／<Metric data={row.postRedemptionUseRate} suffix="%" /></> },
  ];
  const topColumns: TableProps<TopAccountRow>['columns'] = [
    { title: '#', dataIndex: 'rank', width: 42 },
    { title: '帳號', render: (_, row) => canMemberDetail && top ? <Button type="link" className="operations-account-link" onClick={() => setDetail({ kind: 'devices', title: `${row.brand} / ${row.accountLabel} 裝置流量`, brand: row.brand, memberAccountId: row.memberAccountId, snapshotId: top.snapshotId })}>{row.accountLabel}{brand === 'all' && <small className="operations-table-brand"> · {row.brand}</small>}</Button> : <span>{row.accountLabel}{brand === 'all' && <small className="operations-table-brand"> · {row.brand}</small>}</span>, width: 175 },
    { title: '設備數', render: (_, row) => <Metric data={row.deviceCount} />, width: 68 },
    { title: '總流量', render: (_, row) => formatDecimalGb(row.totalBytes), width: 91 },
    { title: 'APK 流量', render: (_, row) => formatDecimalGb(row.apkBytes), width: 95 },
    { title: 'Windows 流量', render: (_, row) => formatDecimalGb(row.windowsBytes), width: 110 },
    { title: '其他流量', render: (_, row) => formatDecimalGb(row.otherBytes), width: 95 },
    { title: '未知流量', render: (_, row) => formatDecimalGb(row.unknownBytes), width: 95 },
    { title: '近 120 秒活動', render: (_, row) => row.estimatedActive?.value == null ? '—' : row.estimatedActive.value ? '有觀測' : '未觀測', width: 105 },
    { title: '最後在線時間', render: (_, row) => row.lastConnectedAt ? new Date(row.lastConnectedAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—', width: 112 },
  ];

  const distribution = summary?.platformDistribution;
  const distributionValues = distribution && [distribution.apkOnly, distribution.windowsOnly, distribution.both, distribution.otherUnknown];
  const actualUseTotal = distributionValues?.every(value => value?.value != null && Number.isFinite(value.value))
    ? distributionValues.reduce((sum, value) => sum + (value.value ?? 0), 0) : null;
  const kpiItems: Array<{ label: string; icon: ReactNode; tone: string; item?: OperationKpi; value?: number | null; share?: OperationKpi; shareLabel?: string }> = [
    { label: '新註冊用戶', icon: <UserAddOutlined />, tone: 'blue', item: summary?.kpis?.newMembers },
    { label: '兌換碼使用人數', icon: <GiftOutlined />, tone: 'purple', item: summary?.kpis?.sameDayRedeemers, share: summary?.kpis?.sameDayRedemptionRate, shareLabel: '兌換率' },
    { label: '最高同時在線', icon: <RiseOutlined />, tone: 'green', item: summary?.kpis?.observedPeakMembers },
    { label: '實際使用會員', icon: <TeamOutlined />, tone: 'teal', value: actualUseTotal },
    { label: 'APK 使用用戶', icon: <AndroidOutlined />, tone: 'mint', item: summary?.kpis?.apkMembers, share: summary?.kpis?.apkShare, shareLabel: '佔比' },
    { label: 'Windows 使用用戶', icon: <WindowsOutlined />, tone: 'blue', item: summary?.kpis?.windowsMembers, share: summary?.kpis?.windowsShare, shareLabel: '佔比' },
  ];
  const cohortChartRows = summary?.cohorts ?? [];
  const today = taipeiWindow(new Date(), 1).through;
  const { title: trendTitle, rows: trendRows } = operationsTrendForRange(range, today, summary?.daily);
  const periodLabel = trendTitle.replace(/關鍵指標趨勢$/, '');
  const earliest = taipeiWindow(new Date(), 30).from;

  return <div data-testid="operations-page" className="operations-page">
    <div className="operations-header">
      <div><Title level={3} style={{ margin: 0 }}>營運數據</Title><Text type="secondary">實時掌握用戶使用情況，監控產品運行狀態</Text></div>
      <div className="operations-controls">
        <Text>日期範圍</Text>
        <RangePicker aria-label="日期區間" value={[dayjs(range.from), dayjs(range.through)]} disabledDate={date => date.format('YYYY-MM-DD') < earliest || date.format('YYYY-MM-DD') > today} onChange={dates => {
          if (!dates?.[0] || !dates[1]) return;
          const from = dates[0].format('YYYY-MM-DD');
          const through = dates[1].format('YYYY-MM-DD');
          const days = Math.round((Date.parse(`${through}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
          if (days >= 1 && days <= 30 && from >= earliest && through <= today) setRange({ from, through });
        }} />
        <Button onClick={() => setRange(taipeiWindow(new Date(), 7))}>近 7 天</Button>
        <Button onClick={() => setRange(taipeiWindow(new Date(), 30))}>近 30 天</Button>
        <Button icon={<ReloadOutlined />} aria-label="刷新" onClick={() => { setTopPage(1); setTopSnapshot(null); setRefresh(value => value + 1); }}>刷新</Button>
      </div>
    </div>
    <div className="operations-meta"><span>臺北時間　{range.from} ～ {range.through}　<Cutoff value={summary?.cutoffAt} /></span><span>「—」表示目前沒有可顯示數值；在線與今日排行不受期間選擇影響。</span></div>
    {capabilities?.canClearAccount && <div className="operations-clear-names"><Switch aria-label="顯示清晰帳號" checked={showClearNames} onChange={value => { setDetail(null); setClearNames(value); }} /> 顯示清晰帳號（每次讀取留稽核）</div>}
    {summaryState?.key === summaryKey && summaryState.error && <Alert type="warning" showIcon message="期間資料未提供" description={summaryState.error} style={{ marginBottom: 12 }} />}

    <section aria-label="期間重點指標" className="operations-kpis">
      <div className="operations-kpi-grid">
        {kpiItems.map(({ label, icon, tone, item, value, share, shareLabel }) => <div key={label} className="operations-kpi-card">
          <div className={`operations-kpi-icon tone-${tone}`}>{icon}</div>
          <div className="operations-kpi-body"><div className="operations-kpi-label">{label}</div>
            <div className="operations-kpi-value">{value === undefined ? <Metric data={item?.current} /> : value === null ? '—' : new Intl.NumberFormat('zh-TW').format(value)}</div>
            <div className="operations-kpi-note">
              {share && <span>{shareLabel} <Metric data={share.current} suffix="%" />；較前期 <Metric data={share.changeRate} suffix="%" /></span>}
              {item ? <span>人數較前期 <Metric data={item.changeRate} suffix="%" /></span> : <span>期間去重</span>}
            </div>
          </div>
        </div>)}
      </div>
      <Text type="secondary" className="operations-footnote">兌換碼使用人數為新註冊用戶於註冊當日成功兌換的人數；最高同時在線為已辨識會員的取樣觀測尖峰，0 不代表確定無人連線。</Text>
    </section>

    <Row gutter={[12, 12]} align="stretch" className="operations-report-row operations-primary-row">
      <Col xs={24} lg={16}>
        <Card title={trendTitle} className="operations-report-card" style={{ height: '100%' }}>
          <OperationsComboChart label="新註冊用戶、兌換碼使用人數與最高同時在線趨勢" dates={trendRows.map(row => row.date)} bars={[
            { label: '新註冊用戶', color: '#559bfa', values: trendRows.map(row => row.newMembers) },
          ]} lines={[
            { label: '兌換碼使用人數', color: '#ad67eb', axis: 'count', values: trendRows.map(row => row.sameDayRedeemers) },
            { label: '最高同時在線', color: '#31bf8b', axis: 'count', values: trendRows.map(row => row.observedPeakMembers) },
          ]} minWidth={650} />
          <Text type="secondary" className="operations-chart-caption">缺值留空；在線尖峰為取樣觀測。</Text>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card title="當前在線用戶" extra={<Cutoff value={online?.asOf} label="最後更新" />} className="operations-report-card operations-online-card" style={{ height: '100%' }}>
          <div className="operations-online-top">
            <div className="operations-online-total"><strong><Metric data={online?.members} /></strong><span>人正在使用 VPN</span></div>
            <div className="operations-online-platform"><AndroidOutlined /><span>APK<strong><Metric data={online?.apkMembers} /></strong></span></div>
            <div className="operations-online-platform"><WindowsOutlined /><span>Windows<strong><Metric data={online?.windowsMembers} /></strong></span></div>
          </div>
          <div className="operations-online-bottom">
            <div><TeamOutlined /><span>在線帳號數<strong><Metric data={online?.members} /></strong></span></div>
            <div><DesktopOutlined /><span>在線設備數<strong><Metric data={online?.devices} /></strong>（含多設備）</span></div>
          </div>
          <Text type="secondary" className="operations-chart-caption">在線人數為已辨識會員的取樣估計，閒置連線可能不在估計內。APK／Windows 裝置：<Metric data={online?.apkDevices} />／<Metric data={online?.windowsDevices} />；其他或未知會員／裝置：<Metric data={online?.unknownMembers} />／<Metric data={online?.unknownDevices} />{brand === 'all' && <>；無法歸屬 flow：{online?.unattributedFlows ?? '—'}</>}。約每 {online?.sampleIntervalSec ?? '—'} 秒取樣，觀測窗口 {online?.activityWindowSec ?? '—'} 秒；閒置或短連線可能未被觀測，0 不代表確定無連線。</Text>
          {onlineState?.key === onlineKey && onlineState.error && <Alert type="warning" showIcon message="即時來源未提供" description={onlineState.error} style={{ marginTop: 8 }} />}
        </Card>
      </Col>
    </Row>

    <Row gutter={[12, 12]} align="stretch" className="operations-report-row operations-secondary-row">
      <Col xs={24} md={12} xl={8}>
        <Card title="新註冊 vs 兌換碼使用" className="operations-report-card" style={{ height: '100%' }}>
          <OperationsComboChart label="新註冊用戶與兌換碼使用人數及兌換率" dates={cohortChartRows.map(row => row.registrationDay)} bars={[
            { label: '新註冊用戶', color: '#559bfa', values: cohortChartRows.map(row => row.registrations) },
            { label: '兌換碼使用人數', color: '#39c896', values: cohortChartRows.map(row => row.sameDayRedeemers) },
          ]} line={{ label: '兌換率', color: '#ff8d35', axis: 'percent', values: cohortChartRows.map(row => row.sameDayRate) }} />
          <Text type="secondary" className="operations-chart-caption">兌換碼使用人數為註冊當日成功兌換的人數；兌換率使用右側 0–100% 刻度。</Text>
        </Card>
      </Col>
      <Col xs={24} md={12} xl={8}>
        <Card title="各平台使用人數 (Daily)" className="operations-report-card" style={{ height: '100%' }}>
          <OperationsComboChart label="每日已辨識 APK 與 Windows 會員" dates={summary?.daily?.map(row => row.date) ?? []} bars={[
            { label: 'APK', color: '#39c896', values: summary?.daily?.map(row => row.apkMembers) ?? [] },
            { label: 'Windows', color: '#43a5f7', values: summary?.daily?.map(row => row.windowsMembers) ?? [] },
          ]} />
          <Text type="secondary" className="operations-chart-caption">同一會員可出現在兩個平台，兩欄不可相加。</Text>
        </Card>
      </Col>
      <Col xs={24} md={12} xl={8}>
        <Card title="設備活躍情況" className="operations-report-card" style={{ height: '100%' }}>
          <Pie data={summary?.platformDistribution} period={range.through === today ? periodLabel : '所選期間'} />
          <Text type="secondary" className="operations-chart-caption">依活躍帳號的平台使用分類，四組互斥；其他／未知使用歸入灰色，APK 與 Windows 指標仍各自計入。</Text>
        </Card>
      </Col>
    </Row>

    <Row gutter={[12, 12]} align="stretch" className="operations-report-row operations-bottom-row">
      <Col xs={24} lg={16}>
        <Card title="今日 Top 100 流量使用（依帳號）" extra={<Cutoff value={top?.cutoffAt} />} className="operations-report-card operations-top-card" style={{ height: '100%' }}>
          {topState?.key === topKey && topState.error && <Alert type="warning" showIcon message="排行榜未提供" description={topState.error} style={{ marginTop: 12 }} />}
          <Table rowKey={row => `${row.brand}-${row.memberAccountId}`} size="small" columns={topColumns} dataSource={top?.rows ?? []} scroll={{ x: 1050, y: 190 }} pagination={false} locale={{ emptyText: '目前沒有可歸屬的會員流量紀錄' }} expandable={{ expandedRowRender: row => <Space wrap>近 120 秒觀測到活動：{formatMetric(row.estimatedActive)}{canMemberDetail && <Button type="link" onClick={() => top && setDetail({ kind: 'devices', title: `${row.brand} / ${row.accountLabel} 裝置流量`, brand: row.brand, memberAccountId: row.memberAccountId, snapshotId: top.snapshotId })}>裝置明細</Button>}</Space> }} />
          <Space style={{ marginTop: 12 }}><Text>最多 100 名；{formatTopAccountCount(top?.total)}</Text><Button disabled={topPage <= 1} onClick={() => setTopPage(page => page - 1)}>上一頁</Button><Text>第 {topPage} 頁</Text><Button disabled={!top || topPage * 20 >= top.total} onClick={() => { if (top && !activeTopSnapshot) setTopSnapshot({ brand, id: top.snapshotId }); setTopPage(page => page + 1); }}>下一頁</Button></Space>
          <Text type="secondary" className="operations-chart-caption">今日 00:00 起可歸屬會員的流量；GB 以十進位換算；最後在線時間採最近成功連線時間。K盾舊制回報另列於下方。</Text>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Card title={`用戶活躍度分布（${periodLabel}）`} className="operations-report-card" style={{ height: '100%' }}>
          <ActivityDayBars buckets={summary?.activityDayBuckets} />
          <Text type="secondary" className="operations-chart-caption">所選期間的使用天數；同一會員同日多裝置只計一次。</Text>
        </Card>
      </Col>
    </Row>

    <Card title="每日與 cohort 明細" extra={<Cutoff value={summary?.cutoffAt} />} style={{ marginBottom: 16 }}>
      <details style={{ marginBottom: 12 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '8px 0' }}>每日會員與裝置明細</summary>
        <Table rowKey="date" columns={dailyColumns} dataSource={summary?.daily ?? []} scroll={{ x: 1100 }} pagination={{ pageSize: 10 }} locale={{ emptyText: '尚無資料' }} />
      </details>
      <details>
        <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '8px 0' }}>註冊 cohort 與兌換後使用明細</summary>
        <Text type="secondary">累計兌換與兌換後使用截至快照時間；兌換後使用須有對應權益的正扣額證據。</Text>
        <Table rowKey="registrationDay" columns={cohortColumns} dataSource={summary?.cohorts ?? []} scroll={{ x: 1100 }} pagination={{ pageSize: 10 }} locale={{ emptyText: '尚無資料' }} />
      </details>
    </Card>

    {brand === 'kshield' && <KshieldLegacyObservations from={range.from} through={range.through} clearNames={showClearNames} refresh={refresh} />}

    <DetailDrawer detail={activeDetail} onClose={() => setDetail(null)} clearNames={showClearNames} />
  </div>;
}

export function OperationsPage() {
  const [range, setRange] = useState(() => taipeiWindow(new Date(), 7));
  const [tab, setTab] = useState('overview');
  return <><Tabs activeKey={tab} onChange={setTab} items={[
    { key: 'overview', label: '營運總覽' }, { key: 'protocol', label: '協議使用' },
  ]} />{tab === 'overview' ? <OperationsOverview range={range} setRange={setRange} /> : <ProtocolUsagePanel range={range} setRange={setRange} />}</>;
}
